import React from 'react';
import { connect } from 'react-redux';
import { Storage } from 'aws-amplify';
import JSZip from 'jszip';
import FaClose from 'react-icons/lib/fa/close';
import FaInfoCircle from 'react-icons/lib/fa/info-circle';
import { STACK, FEATURE_FLAGS } from '../../constants/featureFlags';
import { interventionType } from '../../constants/interventionSelectionConstants';
import { READY_MODULE_TEST_MODES } from '../StudentPreChecks/constants';
import consoleLogger from '../../services/consoleLogger';
import networkLogger from '../../services/networkLogger';
import socketLogger from '../../services/socketLogger';
import { getUserData } from '../../services/API';
import { uuidToHEX } from '../../services/util';
import { clearAsrAssetCaches } from '../../services/asr/asrAssetCache';
import { REDIRECT_FLAG_KEY, isRunningLocally } from '../../services/localhostRedirect';
import { runtimeChecks } from '../../services/asr/engine/runtime_checks';
import { CONSENSUS_DEFAULTS, CONSENSUS_URL_PREFIX } from '../../services/asr/engine/streaming_consensus';
import './DebugMenu.scss';

const UNAVAILABLE_ASR_RUNTIME_CAPS = Object.freeze({
  simd: false,
  sharedArrayBuffer: false,
  crossOriginIsolated: false,
  webGpu: false,
  hardwareConcurrency: 1,
});

/**
 * Probes ASR runtime capabilities for the debug panel without crashing DebugMenu
 * when WebAssembly or related APIs are missing on managed devices.
 * @returns {object}
 */
function getSafeAsrRuntimeCaps() {
  try {
    return runtimeChecks();
  } catch (error) {
    return UNAVAILABLE_ASR_RUNTIME_CAPS;
  }
}

/**
 * Reads the localhost-redirect flag without crashing on managed devices
 * where localStorage access can throw.
 * @returns {boolean}
 */
function getRedirectToLocalhostFlag() {
  try {
    return localStorage.getItem(REDIRECT_FLAG_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

// localStorage key for hiding the "LOCAL" indicator bubble. Stored as a
// hide-flag so the indicator is on by default (absent key = shown).
const HIDE_LOCAL_INDICATOR_KEY = 'debugHideLocalIndicator';

/**
 * Whether to show the "LOCAL" indicator bubble. Defaults to true;
 * storage errors also default to shown.
 * @returns {boolean}
 */
function getShowLocalIndicatorFlag() {
  try {
    return localStorage.getItem(HIDE_LOCAL_INDICATOR_KEY) !== 'true';
  } catch (error) {
    return true;
  }
}

/**
 * Reads consensus threshold values from URL params (prefixed with "consensus_").
 * Returns CONSENSUS_DEFAULTS merged with any URL-specified overrides.
 * @returns {object}
 */
function getConsensusValues() {
  try {
    const params = new URLSearchParams(window.location.search);
    const values = { ...CONSENSUS_DEFAULTS };
    for (const key of Object.keys(CONSENSUS_DEFAULTS)) {
      const val = params.get(CONSENSUS_URL_PREFIX + key);
      if (val !== null) {
        const num = parseFloat(val);
        if (Number.isFinite(num)) {
          values[key] = num;
        }
      }
    }
    return values;
  } catch (_) {
    return { ...CONSENSUS_DEFAULTS };
  }
}

/**
 * Debug menu component that provides a GUI for editing URL parameters.
 * Only visible in DEV/STAGE environments or when showDebug URL param is present.
 * 
 * Allows testers to modify debug parameters without manually editing URLs.
 * On save, performs a full page reload with the updated parameters.
 */
class DebugMenu extends React.Component {
  constructor(props) {
    super(props);
    
    // Capture initial URL params
    const urlParams = new URLSearchParams(window.location.search);
    const allParams = {};
    
    // Store all URL params that were present on initial load
    for (const [key, value] of urlParams.entries()) {
      allParams[key] = value;
    }
    
    this.state = {
      isModalOpen: false,
      isExporting: false,
      isClearingAsrCache: false,
      // Capabilities are fixed for the page lifetime (COOP/COEP, SIMD, cores).
      // Do not re-compute on re-render or in componentDidUpdate.
      asrRuntimeCaps: getSafeAsrRuntimeCaps(),
      initialParams: allParams,
      // Editable debug params
      userId: urlParams.get('userId') || '',
      districtId: urlParams.get('districtId') || '',
      schoolId: urlParams.get('schoolId') || '',
      grade: urlParams.get('grade') || '',
      isAuthorizedIp: urlParams.get('isAuthorizedIp') || '',
      storyId: urlParams.get('storyId') || '',
      interventionType: urlParams.get('interventionType') || '',
      readyModuleTestMode: urlParams.get('readyModuleTestMode') || '',
      interventionSelectionWCPM: urlParams.get('interventionSelectionWCPM') || '',
      interventionSelectionARM: urlParams.get('interventionSelectionARM') || '',
      assignmentId: urlParams.get('assignmentId') || '',
      assignmentType: urlParams.get('assignmentType') || '',
      tutorLocale: urlParams.get('tutorLocale') || '',
      googlePM: urlParams.get('googlePM') === 'true' ? 'true' : 'false',
      midSessionTroubleshootSeen: urlParams.get('midSessionTroubleshootSeen') === 'true' ? 'true' : 'false',
      suppressMidSessionTroubleshoot: urlParams.get('suppressMidSessionTroubleshoot') === 'true' ? 'true' : 'false',
      forceUserTheta: urlParams.get('forceUserTheta') || '',
      forceClippedAudio: urlParams.get('forceClippedAudio') === 'true' ? 'true' : 'false',
      forceStaticAudio: urlParams.get('forceStaticAudio') === 'true' ? 'true' : 'false',
      forceLowAudio: urlParams.get('forceLowAudio') === 'true' ? 'true' : 'false',
      // TT-1230's two high-precision branches, the only route to bad-signal cursor-free
      forceDeadSilenceAudio: urlParams.get('forceDeadSilenceAudio') === 'true' ? 'true' : 'false',
      forcePervasiveClippingAudio: urlParams.get('forcePervasiveClippingAudio') === 'true' ? 'true' : 'false',
      forceAllUnks: urlParams.get('forceAllUnks') === 'true' ? 'true' : 'false',
      forceBrand: urlParams.get('forceBrand') || '',
      forceBrowserAsr: urlParams.get('forceBrowserAsr') === 'true' ? 'true' : urlParams.get('forceBrowserAsr') === 'false' ? 'false' : '',
      forceTapToRead: urlParams.get('forceTapToRead') === 'true' ? 'true' : 'false',
      forceCursorFree: urlParams.get('forceCursorFree') === 'true' ? 'true' : urlParams.get('forceCursorFree') === 'false' ? 'false' : '',
      // Tri-state: '' leaves the district entitlement in charge, so it round-trips both values
      forceBadSignalCursor: urlParams.get('forceBadSignalCursor') === 'true' ? 'true' : urlParams.get('forceBadSignalCursor') === 'false' ? 'false' : '',
      forceBadSignalLogOnly: urlParams.get('forceBadSignalLogOnly') === 'true' ? 'true' : urlParams.get('forceBadSignalLogOnly') === 'false' ? 'false' : '',
      forceLocalAsrWarmup: urlParams.get('forceLocalAsrWarmup') === 'true' ? 'true' : 'false',
      forceErssEnglish: urlParams.get('forceErssEnglish') === 'true' ? 'true' : 'false',
      forceErssSpanish: urlParams.get('forceErssSpanish') === 'true' ? 'true' : 'false',
      // Not a URL param: per-browser localStorage flag consumed by app.js at boot
      redirectToLocalhost: getRedirectToLocalhostFlag(),
      // "LOCAL" bubble when served from localhost; on by default
      showLocalIndicator: getShowLocalIndicatorFlag(),
      // Consensus threshold overrides (URL param based, requires Save and Reload)
      consensusValues: getConsensusValues(),
      // Browser ASR decode mode: streaming is the default; this re-enables the
      // legacy rolling-window + consensus path (URL param `asr_window=1`)
      asrWindowMode: urlParams.get('asr_window') === '1',
    };
    
    this.toggleModal = this.toggleModal.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleExportLogs = this.handleExportLogs.bind(this);
    this.handleClearAsrAssetCache = this.handleClearAsrAssetCache.bind(this);
    this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
    this.uploadDebugFileToS3 = this.uploadDebugFileToS3.bind(this);
    this.handleRedirectToLocalhostChange = this.handleRedirectToLocalhostChange.bind(this);
    this.handleShowLocalIndicatorChange = this.handleShowLocalIndicatorChange.bind(this);
    this.handleConsensusChange = this.handleConsensusChange.bind(this);
    this.handleConsensusReset = this.handleConsensusReset.bind(this);
    
    // Initialize loggers if debug menu is visible
    if (this.shouldShowDebugMenu()) {
      consoleLogger.init();
      networkLogger.init();
      socketLogger.init();
    }
  }
  
  /**
   * Determines if the debug menu should be visible.
   * Visible if in DEV/STAGE environment or showDebug URL param is present.
   */
  shouldShowDebugMenu() {
    const stack = process.env.FEATURE_FLAGS_STACK;
    
    // Check if environment is DEV or STAGE
    if (stack === STACK.DEV || stack === STACK.STAGE) {
      return true;
    }
    
    // Check for showDebug URL param
    const urlParams = new URLSearchParams(window.location.search);
    const showDebug = urlParams.get('showDebug');
    return showDebug === '1' || showDebug === 'true';
  }
  
  toggleModal() {
    this.setState({ isModalOpen: !this.state.isModalOpen });
  }
  
  handleInputChange(field, value) {
    this.setState({ [field]: value });
  }
  
  /**
   * Exports debug logs as 3 separate JSON files:
   * - console-logs.json: Console logs with timestamps
   * - socket-events.json: Socket events with timestamps
   * - network-errors.json: Network failures with timestamps
   * 
   * Files are uploaded to S3 and packaged into a .zip for local download.
   */
  async handleExportLogs() {
    const userData = getUserData();
    const studentId = userData?.userId || userData?.attributes?.['custom:sisId'] || null;
    const activityId = userData?.activityId || null;
    const storyId = this.props.currentStoryId || null;
    const timestamp = new Date().getTime();
    
    // Common metadata for all files
    const metadata = {
      exportInfo: {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        stack: process.env.STACK || 'UNKNOWN',
      },
      session: {
        studentId,
        activityId,
        storyId,
      },
    };
    
    // Get console logs
    const consoleLogs = consoleLogger.getLogs();
    const consoleLogsData = {
      ...metadata,
      totalLogs: consoleLogs.length,
      logsByType: {
        log: consoleLogs.filter(l => l.type === 'log').length,
        warn: consoleLogs.filter(l => l.type === 'warn').length,
        error: consoleLogs.filter(l => l.type === 'error').length,
        info: consoleLogs.filter(l => l.type === 'info').length,
        session: consoleLogs.filter(l => l.type === 'session').length,
      },
      logs: consoleLogs.map(log => ({
        timestamp: log.timestamp,
        type: log.type,
        message: log.message,
      })),
    };
    
    // Get socket events
    const socketEvents = socketLogger.getEvents();
    const socketStats = socketLogger.getStats();
    const socketEventsData = {
      ...metadata,
      totalEvents: socketStats.total,
      eventsByDirection: {
        outgoing: socketStats.outgoing,
        incoming: socketStats.incoming,
        connection: socketStats.connection,
      },
      events: socketEvents.map(event => ({
        timestamp: event.timestamp,
        direction: event.direction,
        event: event.event,
        service: event.service || null,
        payload: event.payload,
      })),
    };
    
    // Get network errors
    const networkErrors = networkLogger.getFailedRequests();
    const networkErrorsData = {
      ...metadata,
      totalErrors: networkErrors.length,
      errorsByType: {
        httpErrors: networkErrors.filter(e => e.type === 'HTTP_ERROR').length,
        graphqlErrors: networkErrors.filter(e => e.type === 'GRAPHQL_ERROR' || e.type === 'GRAPHQL_REQUEST_ERROR').length,
        networkErrors: networkErrors.filter(e => e.type === 'NETWORK_ERROR').length,
      },
      errorsBySource: {
        fetch: networkErrors.filter(e => !e.source || e.source === 'fetch').length,
        xhr: networkErrors.filter(e => e.source === 'XHR').length,
        manual: networkErrors.filter(e => e.serviceName && e.source !== 'XHR').length,
      },
      errors: networkErrors.map(req => ({
        timestamp: req.timestamp,
        type: req.type,
        url: req.url,
        method: req.method,
        status: req.status || null,
        statusText: req.statusText || null,
        service: req.serviceName || null,
        duration: req.duration || null,
        errorMessage: req.errorMessage || null,
        errorBody: req.errorBody || null,
        graphqlErrors: req.graphqlErrors || null,
        source: req.source || 'fetch',
      })),
    };
    
    // Create JSON strings
    const consoleLogsJson = JSON.stringify(consoleLogsData, null, 2);
    const socketEventsJson = JSON.stringify(socketEventsData, null, 2);
    const networkErrorsJson = JSON.stringify(networkErrorsData, null, 2);
    
    // File names
    const consoleLogsFilename = `debug-console-logs-${timestamp}.json`;
    const socketEventsFilename = `debug-socket-events-${timestamp}.json`;
    const networkErrorsFilename = `debug-network-errors-${timestamp}.json`;
    
    // Set loading state
    this.setState({ isExporting: true });
    
    try {
      // Upload all 3 files to S3 in parallel and wait for completion
      await Promise.all([
        this.uploadDebugFileToS3(consoleLogsJson, consoleLogsFilename, activityId, studentId),
        this.uploadDebugFileToS3(socketEventsJson, socketEventsFilename, activityId, studentId),
        this.uploadDebugFileToS3(networkErrorsJson, networkErrorsFilename, activityId, studentId),
      ]);
      
      // Create zip file for local download
      const zip = new JSZip();
      zip.file(consoleLogsFilename, consoleLogsJson);
      zip.file(socketEventsFilename, socketEventsJson);
      zip.file(networkErrorsFilename, networkErrorsJson);
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFilename = `debug-logs-${timestamp}.zip`;
      
      // Download the zip file
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Debug logs exported:', zipFilename);
      
      // Flush all loggers to free memory - only new logs will be captured from now on
      consoleLogger.clearLogs();
      networkLogger.clearLogs();
      socketLogger.clearEvents();
    } catch (err) {
      console.error('Failed to export debug logs:', err);
    } finally {
      // Clear loading state
      this.setState({ isExporting: false });
    }
  }
  
  /**
   * Uploads a single debug file to S3 in the same bucket/folder as logSessionInfo
   * Uses activityId as the folder, or falls back to hex-encoded studentId
   */
  async uploadDebugFileToS3(jsonContent, filename, activityId, studentId) {
    // Determine the activity/folder - same logic as logSessionInfo
    let activity = activityId;
    if (!activity && studentId) {
      activity = uuidToHEX(studentId);
    }
    
    if (!activity) {
      console.warn('Debug file S3 upload skipped: no activityId or studentId available');
      return;
    }
    
    const options = {
      bucket: process.env.LOGGINGBUCKET + '/' + activity,
      contentType: 'application/json',
      level: 'public',
      region: process.env.S3_BUCKET_REGION || 'us-east-2',
      customPrefix: {
        public: '',
      },
    };
    
    try {
      await Storage.put(filename, jsonContent, options);
      console.log('Debug file uploaded to S3:', filename);
    } catch (err) {
      console.error('Debug file S3 upload failed:', err);
    }
  }

  /**
   * Clears cached ASR model/runtime assets. Debug-only; no-op when Cache Storage
   * is unavailable. Does not reload the page — reload manually to re-run preload.
   */
  async handleClearAsrAssetCache() {
    if (this.state.isClearingAsrCache) {
      return;
    }

    this.setState({ isClearingAsrCache: true });
    try {
      const deleted = await clearAsrAssetCaches();
      if (deleted.length === 0) {
        alert('No ASR asset caches found (or Cache Storage is unavailable).');
      } else {
        alert(`Cleared ASR asset cache: ${deleted.join(', ')}`);
      }
    } catch (err) {
      alert(`Failed to clear ASR caches: ${err && err.message ? err.message : String(err)}`);
    } finally {
      this.setState({ isClearingAsrCache: false });
    }
  }

  /**
   * Toggles the "Redirect launches to localhost" flag. Takes effect immediately
   * (persisted to localStorage, consumed by app.js at boot) — deliberately not
   * part of Save and Reload's URL params so it can never leak into session URLs.
   */
  handleRedirectToLocalhostChange(event) {
    const enabled = event.target.checked;
    try {
      if (enabled) {
        localStorage.setItem(REDIRECT_FLAG_KEY, 'true');
      } else {
        localStorage.removeItem(REDIRECT_FLAG_KEY);
      }
      this.setState({ redirectToLocalhost: enabled });
    } catch (err) {
      alert('Could not persist the setting (localStorage unavailable).');
    }
  }

  /**
   * Toggles the "LOCAL" indicator bubble. Persisted as a hide-flag so the
   * indicator is shown by default.
   */
  handleShowLocalIndicatorChange(event) {
    const shown = event.target.checked;
    try {
      if (shown) {
        localStorage.removeItem(HIDE_LOCAL_INDICATOR_KEY);
      } else {
        localStorage.setItem(HIDE_LOCAL_INDICATOR_KEY, 'true');
      }
      this.setState({ showLocalIndicator: shown });
    } catch (err) {
      alert('Could not persist the setting (localStorage unavailable).');
    }
  }

  /**
   * Updates a single consensus threshold value in component state.
   * Takes effect after Save and Reload (written to URL params by handleSave).
   */
  handleConsensusChange(key, value) {
    const numValue = parseFloat(value);
    if (!Number.isFinite(numValue)) return;
    const updated = { ...this.state.consensusValues, [key]: numValue };
    this.setState({ consensusValues: updated });
  }

  /**
   * Resets all consensus thresholds back to their hardcoded defaults.
   */
  handleConsensusReset() {
    this.setState({ consensusValues: { ...CONSENSUS_DEFAULTS } });
  }

  /**
   * Copies text to clipboard
   */
  async handleCopyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard. Please copy manually.');
    }
  }
  
  /**
   * Builds a new URL with updated parameters and reloads the page.
   * Merges initial params with edited debug params, removing empty values.
   */
  handleSave() {
    const debugParams = {
      userId: this.state.userId,
      districtId: this.state.districtId,
      schoolId: this.state.schoolId,
      grade: this.state.grade,
      isAuthorizedIp: this.state.isAuthorizedIp,
      storyId: this.state.storyId,
      interventionType: this.state.interventionType,
      readyModuleTestMode: this.state.readyModuleTestMode,
      interventionSelectionWCPM: this.state.interventionSelectionWCPM,
      interventionSelectionARM: this.state.interventionSelectionARM,
      assignmentId: this.state.assignmentId,
      assignmentType: this.state.assignmentType,
      tutorLocale: this.state.tutorLocale,
    };
    
    // Start with all initial params
    const newParams = { ...this.state.initialParams };
    
    // Update with edited debug params, removing empty values
    Object.keys(debugParams).forEach(key => {
      if (debugParams[key]) {
        newParams[key] = debugParams[key];
      } else {
        // Remove the param if it's been cleared
        delete newParams[key];
      }
    });
    
    // Handle googlePM specially - only include if true, otherwise remove
    if (this.state.googlePM === 'true') {
      newParams.googlePM = 'true';
    } else {
      delete newParams.googlePM;
    }
    
    if (this.state.midSessionTroubleshootSeen === 'true') {
      newParams.midSessionTroubleshootSeen = 'true';
    } else {
      delete newParams.midSessionTroubleshootSeen;
    }
    
    if (this.state.suppressMidSessionTroubleshoot === 'true') {
      newParams.suppressMidSessionTroubleshoot = 'true';
    } else {
      delete newParams.suppressMidSessionTroubleshoot;
    }
    
    if (this.state.forceClippedAudio === 'true') {
      newParams.forceClippedAudio = 'true';
    } else {
      delete newParams.forceClippedAudio;
    }
    
    if (this.state.forceStaticAudio === 'true') {
      newParams.forceStaticAudio = 'true';
    } else {
      delete newParams.forceStaticAudio;
    }
    
    if (this.state.forceLowAudio === 'true') {
      newParams.forceLowAudio = 'true';
    } else {
      delete newParams.forceLowAudio;
    }

    if (this.state.forceDeadSilenceAudio === 'true') {
      newParams.forceDeadSilenceAudio = 'true';
    } else {
      delete newParams.forceDeadSilenceAudio;
    }

    if (this.state.forcePervasiveClippingAudio === 'true') {
      newParams.forcePervasiveClippingAudio = 'true';
    } else {
      delete newParams.forcePervasiveClippingAudio;
    }

    if (this.state.forceAllUnks === 'true') {
      newParams.forceAllUnks = 'true';
    } else {
      delete newParams.forceAllUnks;
    }
    
    if (this.state.forceTapToRead === 'true') {
      newParams.forceTapToRead = 'true';
    } else {
      delete newParams.forceTapToRead;
    }

    if (this.state.forceCursorFree === 'true' || this.state.forceCursorFree === 'false') {
      newParams.forceCursorFree = this.state.forceCursorFree;
    } else {
      delete newParams.forceCursorFree;
    }

    // Both values are meaningful here, so keep 'false' in the URL and drop only the unset case.
    if (this.state.forceBadSignalCursor === 'true' || this.state.forceBadSignalCursor === 'false') {
      newParams.forceBadSignalCursor = this.state.forceBadSignalCursor;
    } else {
      delete newParams.forceBadSignalCursor;
    }

    if (this.state.forceBadSignalLogOnly === 'true' || this.state.forceBadSignalLogOnly === 'false') {
      newParams.forceBadSignalLogOnly = this.state.forceBadSignalLogOnly;
    } else {
      delete newParams.forceBadSignalLogOnly;
    }

    if (this.state.forceLocalAsrWarmup === 'true') {
      newParams.forceLocalAsrWarmup = 'true';
    } else {
      delete newParams.forceLocalAsrWarmup;
    }

    if (this.state.forceErssEnglish === 'true') {
      newParams.forceErssEnglish = 'true';
    } else {
      delete newParams.forceErssEnglish;
    }

    if (this.state.forceErssSpanish === 'true') {
      newParams.forceErssSpanish = 'true';
    } else {
      delete newParams.forceErssSpanish;
    }
    
    // Handle forceBrand - only include when a brand is selected
    if (this.state.forceBrand) {
      newParams.forceBrand = this.state.forceBrand;
    } else {
      delete newParams.forceBrand;
    }
    
    // Handle forceUserTheta - only include when non-empty (consistent with other overrides)
    if (this.state.forceUserTheta && this.state.forceUserTheta.trim()) {
      newParams.forceUserTheta = this.state.forceUserTheta.trim();
    } else {
      delete newParams.forceUserTheta;
    }

    if (this.state.forceBrowserAsr === 'true' || this.state.forceBrowserAsr === 'false') {
      newParams.forceBrowserAsr = this.state.forceBrowserAsr;
    } else {
      delete newParams.forceBrowserAsr;
    }

    if (this.state.asrWindowMode) {
      newParams.asr_window = '1';
    } else {
      delete newParams.asr_window;
    }

    // A leftover asr_scalar=1 from an old session must not persist: the scalar
    // vosk-v2 runtime is no longer supported (SIMD is required at the enablement
    // gate; non-SIMD devices use server Kaldi).
    delete newParams.asr_scalar;

    // Consensus threshold overrides — only include non-default values
    for (const key of Object.keys(CONSENSUS_DEFAULTS)) {
      const paramKey = CONSENSUS_URL_PREFIX + key;
      if (this.state.consensusValues[key] !== CONSENSUS_DEFAULTS[key]) {
        newParams[paramKey] = String(this.state.consensusValues[key]);
      } else {
        delete newParams[paramKey];
      }
    }

    // Build new URL
    const searchParams = new URLSearchParams(newParams);
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const newUrl = `${origin}${pathname}?${searchParams.toString()}`;
    
    // Reload page with new params
    window.location.href = newUrl;
  }
  
  render() {
    if (!this.shouldShowDebugMenu()) {
      return null;
    }
    
    // Get intervention type options - filter out internal/hidden types, sort for the dropdown
    const interventionTypeOptions = Object.values(interventionType)
      .filter(type => !['readwithme', 'endReadWithMe'].includes(type))
      .sort((a, b) => a.localeCompare(b));
    
    // Get ready module test mode options
    const readyModuleTestModeOptions = Object.values(READY_MODULE_TEST_MODES);
    
    // Assignment type options
    const assignmentTypeOptions = [
      'BENCHMARK',
      'PROGRESS_MONITORING',
      'CALIBRATION',
      'TUTOR',
      'CURRICULUM'
    ];
    
    // Get log statistics for display
    const logStats = consoleLogger.getLogStats();
    const networkStats = networkLogger.getStats();
    const socketStats = socketLogger.getStats();
    
    // Get current session data
    const userData = getUserData();
    const userId = userData?.userId || userData?.attributes?.['custom:sisId'] || 'N/A';
    const activityId = userData?.activityId || 'N/A';
    // Get storyId from Redux store (passed via props from connect())
    const storyId = this.props.currentStoryId || 'N/A';
    const districtId = userData?.districtId || 'N/A';
    const schoolId = userData?.schoolId || 'N/A';
    
    return (
      <>
        {isRunningLocally() && this.state.showLocalIndicator && (
          <div className='debug-menu-local-indicator' aria-hidden='true'>
            LOCAL
          </div>
        )}
        <button
          className='debug-menu-button'
          onClick={this.toggleModal}
          aria-label='Open Debug Menu'
        >
          🛠️
        </button>
        
        {this.state.isModalOpen && (
          <div className='debug-menu-overlay'>
            <div className='debug-menu-modal'>
              <button 
                className='debug-menu-close-button'
                onClick={this.toggleModal}
                aria-label='Close debug menu'
              >
                <FaClose size='24' />
              </button>
              <h2 className='debug-menu-title'>Debug Menu</h2>
              
              <div className='debug-menu-session-info'>
                <h3 className='debug-menu-session-title'>Current Session Info</h3>
                
                <div className='debug-menu-info-row'>
                  <label>Student ID:</label>
                  <span className='debug-menu-info-value' title={userId}>{userId}</span>
                  <button 
                    className='debug-menu-copy-button'
                    onClick={() => this.handleCopyToClipboard(userId, 'Student ID')}
                    disabled={userId === 'N/A'}
                    aria-label='Copy Student ID'
                  >
                    📋
                  </button>
                </div>
                
                <div className='debug-menu-info-row'>
                  <label>Activity ID:</label>
                  <span className='debug-menu-info-value' title={activityId}>{activityId}</span>
                  <button 
                    className='debug-menu-copy-button'
                    onClick={() => this.handleCopyToClipboard(activityId, 'Activity ID')}
                    disabled={activityId === 'N/A'}
                    aria-label='Copy Activity ID'
                  >
                    📋
                  </button>
                </div>
                
                <div className='debug-menu-info-row'>
                  <label>Story ID:</label>
                  <span className='debug-menu-info-value' title={storyId}>{storyId}</span>
                  <button
                    className='debug-menu-copy-button'
                    onClick={() => this.handleCopyToClipboard(storyId, 'Story ID')}
                    disabled={storyId === 'N/A'}
                    aria-label='Copy Story ID'
                  >
                    📋
                  </button>
                </div>

                <div className='debug-menu-info-row'>
                  <label>District ID:</label>
                  <span className='debug-menu-info-value' title={districtId}>{districtId}</span>
                  <button
                    className='debug-menu-copy-button'
                    onClick={() => this.handleCopyToClipboard(districtId, 'District ID')}
                    disabled={districtId === 'N/A'}
                    aria-label='Copy District ID'
                  >
                    📋
                  </button>
                </div>

                <div className='debug-menu-info-row'>
                  <label>School ID:</label>
                  <span className='debug-menu-info-value' title={schoolId}>{schoolId}</span>
                  <button
                    className='debug-menu-copy-button'
                    onClick={() => this.handleCopyToClipboard(schoolId, 'School ID')}
                    disabled={schoolId === 'N/A'}
                    aria-label='Copy School ID'
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className='debug-menu-export-section'>
                <h3 className='debug-menu-export-title'>Export Debug Logs</h3>
                <p className='debug-menu-export-info'>
                  {logStats.total} console logs, {networkStats.total} network errors, {socketStats.total} socket events
                </p>
                <button
                  className={`debug-menu-export-button ${this.state.isExporting ? 'exporting' : ''}`}
                  onClick={this.handleExportLogs}
                  disabled={this.state.isExporting}
                >
                  {this.state.isExporting ? (
                    <>
                      <span className='debug-menu-spinner'></span>
                      Uploading...
                    </>
                  ) : (
                    '📥 Export Logs'
                  )}
                </button>
              </div>

              <div className='debug-menu-url-params-section'>
                <h3 className='debug-menu-section-title'>Force Parameters</h3>
                <div className='debug-menu-form'>
                <div className='debug-menu-field'>
                  <label htmlFor='userId'>
                    Student ID
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Current student ID. Can replace this with a different student ID to test with a different student (may want to update the district/school ID to appropriate values).</span>
                    </span>
                  </label>
                  <input 
                    id='userId'
                    type='text' 
                    value={this.state.userId}
                    onChange={(e) => this.handleInputChange('userId', e.target.value)}
                  />
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='districtId'>
                    District ID
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Current district ID. Can replace this with a different district ID to test with a different district (will need to update the school ID to a school within that district).</span>
                    </span>
                  </label>
                  <input 
                    id='districtId'
                    type='text' 
                    value={this.state.districtId}
                    onChange={(e) => this.handleInputChange('districtId', e.target.value)}
                  />
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='schoolId'>
                    School ID
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Current school ID. Can replace this with a different school ID to test with a different school (will need to update the district ID to the district that contains that school).</span>
                    </span>
                  </label>
                  <input 
                    id='schoolId'
                    type='text' 
                    value={this.state.schoolId}
                    onChange={(e) => this.handleInputChange('schoolId', e.target.value)}
                  />
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='grade'>
                    Grade
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Edit the grade that is used for this user in the session. This will influence things like intervention selection and default WCPM/ARM.</span>
                    </span>
                  </label>
                  <select 
                    id='grade'
                    value={this.state.grade}
                    onChange={(e) => this.handleInputChange('grade', e.target.value)}
                  >
                    <option value=''>-- Select --</option>
                    <option value='-1'>-1</option>
                    <option value='0'>0</option>
                    <option value='1'>1</option>
                    <option value='2'>2</option>
                    <option value='3'>3</option>
                    <option value='4'>4</option>
                    <option value='5'>5</option>
                    <option value='6'>6</option>
                    <option value='7'>7</option>
                    <option value='8'>8</option>
                  </select>
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='isAuthorizedIp'>
                    On Campus
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Can test forcing a user to be on campus or off campus. If a district is not set up to use authorized IP addresses, then users are always "on campus".</span>
                    </span>
                  </label>
                  <select 
                    id='isAuthorizedIp'
                    value={this.state.isAuthorizedIp}
                    onChange={(e) => this.handleInputChange('isAuthorizedIp', e.target.value)}
                  >
                    <option value=''>-- Select --</option>
                    <option value='true'>true</option>
                    <option value='false'>false</option>
                  </select>
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='readyModuleTestMode'>
                    Ready Module Mode
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Test the ready module in different scenarios. SKIP will skip the ready module entirely (even when an assessment is assigned).</span>
                    </span>
                  </label>
                  <select 
                    id='readyModuleTestMode'
                    value={this.state.readyModuleTestMode}
                    onChange={(e) => this.handleInputChange('readyModuleTestMode', e.target.value)}
                  >
                    <option value=''>-- Select --</option>
                    {readyModuleTestModeOptions.map(mode => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                </div>

                <div className='debug-menu-field'>
                  <label htmlFor='storyId'>
                    Force Story ID
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Force a specific story to be played.</span>
                    </span>
                  </label>
                  <input 
                    id='storyId'
                    type='text' 
                    value={this.state.storyId}
                    onChange={(e) => this.handleInputChange('storyId', e.target.value)}
                  />
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='interventionSelectionWCPM'>
                    Force WCPM
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Force a specific historic WCPM for the session. This will influence word-level intervention suppression, intervention selection, and struggle monitor.</span>
                    </span>
                  </label>
                  <input 
                    id='interventionSelectionWCPM'
                    type='text' 
                    value={this.state.interventionSelectionWCPM}
                    onChange={(e) => this.handleInputChange('interventionSelectionWCPM', e.target.value)}
                  />
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='interventionSelectionARM'>
                    Force ARM
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Force a specific historic ARM for the session. This will influence intervention selection and struggle monitor.</span>
                    </span>
                  </label>
                  <input 
                    id='interventionSelectionARM'
                    type='text' 
                    value={this.state.interventionSelectionARM}
                    onChange={(e) => this.handleInputChange('interventionSelectionARM', e.target.value)}
                  />
                </div>

                <div className='debug-menu-field'>
                  <label htmlFor='interventionType'>
                    Force Intervention
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>Force a specific intervention type for the session every time an intervention is triggered.</span>
                    </span>
                  </label>
                  <select 
                    id='interventionType'
                    value={this.state.interventionType}
                    onChange={(e) => this.handleInputChange('interventionType', e.target.value)}
                  >
                    <option value=''>-- Select --</option>
                    {interventionTypeOptions.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div className='debug-menu-field'>
                  <label htmlFor='assignmentId'>
                    Assignment ID
                    <span className='debug-menu-tooltip'>
                      <FaInfoCircle />
                      <span className='debug-menu-tooltiptext'>The assignment ID used in this session. Can replace this with a different assignment ID to test with a different assignment. Make sure to update the assignment type to the appropriate type for the assignment ID.</span>
                    </span>
                  </label>
                  <input 
                    id='assignmentId'
                    type='text' 
                    value={this.state.assignmentId}
                    onChange={(e) => this.handleInputChange('assignmentId', e.target.value)}
                  />
                </div>
                
                  <div className='debug-menu-field'>
                    <label htmlFor='assignmentType'>
                      Assignment Type
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>The type of assignment used in this session. If assignment ID exists, should match the assignment type of the assignment ID. If no assignment ID, then should be blank.</span>
                      </span>
                    </label>
                    <select 
                      id='assignmentType'
                      value={this.state.assignmentType}
                      onChange={(e) => this.handleInputChange('assignmentType', e.target.value)}
                    >
                      <option value=''>-- Select --</option>
                      {assignmentTypeOptions.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='tutorLocale'>
                      Tutor Locale
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force a specific tutor locale for the session. This determines the story language used by the tutor.</span>
                      </span>
                    </label>
                    <select 
                      id='tutorLocale'
                      value={this.state.tutorLocale}
                      onChange={(e) => this.handleInputChange('tutorLocale', e.target.value)}
                    >
                      <option value=''>-- Select --</option>
                      <option value='en-us'>en-us</option>
                      <option value='es-mx'>es-mx</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceErssEnglish'>
                      Force English ERSS
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force the student into English early-reader (ERSS) mode for this session without changing the student&apos;s early_reader metadata. Requires the ERSS service to return an English sequence for the student (e.g. a grade PK/K/1 test account).</span>
                      </span>
                    </label>
                    <select
                      id='forceErssEnglish'
                      value={this.state.forceErssEnglish}
                      onChange={(e) => this.handleInputChange('forceErssEnglish', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceErssSpanish'>
                      Force Spanish ERSS
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force the student into Spanish early-reader (ERSS) mode for this session without changing the student&apos;s spanish_early_reader metadata. Requires the ERSS service to return a Spanish sequence for the student.</span>
                      </span>
                    </label>
                    <select
                      id='forceErssSpanish'
                      value={this.state.forceErssSpanish}
                      onChange={(e) => this.handleInputChange('forceErssSpanish', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='googlePM'>
                      Use Google PM
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Enable or disable Google PM for this session.</span>
                      </span>
                    </label>
                    <select 
                      id='googlePM'
                      value={this.state.googlePM}
                      onChange={(e) => this.handleInputChange('googlePM', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='midSessionTroubleshootSeen'>
                      Mid-Session Troubleshoot Seen
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Simulate that the user has already been through the mid-session audio troubleshoot flow once. When true, a second troubleshoot trigger will show the repeat interstitial with options to troubleshoot again, continue, or end session.</span>
                      </span>
                    </label>
                    <select 
                      id='midSessionTroubleshootSeen'
                      value={this.state.midSessionTroubleshootSeen}
                      onChange={(e) => this.handleInputChange('midSessionTroubleshootSeen', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='suppressMidSessionTroubleshoot'>
                      Suppress Mid-Session Troubleshoot
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Prevent mid-session audio troubleshoot from triggering for the remainder of the session. Simulates the "Allow Student to Continue Session" choice from the repeat troubleshoot interstitial.</span>
                      </span>
                    </label>
                    <select 
                      id='suppressMidSessionTroubleshoot'
                      value={this.state.suppressMidSessionTroubleshoot}
                      onChange={(e) => this.handleInputChange('suppressMidSessionTroubleshoot', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceUserTheta'>
                      Force User Theta
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Override the user theta value for the session. Theta is a reading ability score used in audio issue detection thresholds.</span>
                      </span>
                    </label>
                    <input 
                      id='forceUserTheta'
                      type='text' 
                      value={this.state.forceUserTheta}
                      onChange={(e) => this.handleInputChange('forceUserTheta', e.target.value)}
                    />
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceClippedAudio'>
                      Force Clipped Audio
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force the clipped audio condition to true in audio issue detection, regardless of actual audio metrics. Also populates the bad-signal cursor branch&apos;s prior-activity signal with this measurement when that branch is on; it cannot reach a cursor mode on its own.</span>
                      </span>
                    </label>
                    <select 
                      id='forceClippedAudio'
                      value={this.state.forceClippedAudio}
                      onChange={(e) => this.handleInputChange('forceClippedAudio', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceStaticAudio'>
                      Force Static Audio
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force the static low band audio condition to true in audio issue detection, regardless of actual audio metrics. Also populates the bad-signal cursor branch&apos;s prior-activity signal with this measurement when that branch is on; it cannot reach a cursor mode on its own.</span>
                      </span>
                    </label>
                    <select 
                      id='forceStaticAudio'
                      value={this.state.forceStaticAudio}
                      onChange={(e) => this.handleInputChange('forceStaticAudio', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceLowAudio'>
                      Force Low Audio
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force the low volume audio condition to true in audio issue detection, regardless of actual audio metrics. Also populates the bad-signal cursor branch&apos;s prior-activity signal with this measurement when that branch is on; it cannot reach a cursor mode on its own.</span>
                      </span>
                    </label>
                    <select 
                      id='forceLowAudio'
                      value={this.state.forceLowAudio}
                      onChange={(e) => this.handleInputChange('forceLowAudio', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceDeadSilenceAudio'>
                      Force Dead Silence
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Fabricates TT-1230&apos;s &quot;dead silence&quot; high-precision audio branch on the prior-activity signal. With the bad-signal cursor branch on and WCPM above the pivot, this is one of the only two ways to reach cursor-free.</span>
                      </span>
                    </label>
                    <select
                      id='forceDeadSilenceAudio'
                      value={this.state.forceDeadSilenceAudio}
                      onChange={(e) => this.handleInputChange('forceDeadSilenceAudio', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forcePervasiveClippingAudio'>
                      Force Pervasive Clipping
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Fabricates TT-1230&apos;s &quot;pervasive clipping&quot; high-precision audio branch on the prior-activity signal. The other of the two routes to bad-signal cursor-free.</span>
                      </span>
                    </label>
                    <select
                      id='forcePervasiveClippingAudio'
                      value={this.state.forcePervasiveClippingAudio}
                      onChange={(e) => this.handleInputChange('forcePervasiveClippingAudio', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceAllUnks'>
                      Force All UNKs in Transcript
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force the invalid transcript condition to true in audio issue detection, simulating all transcripts containing only UNK tokens.</span>
                      </span>
                    </label>
                    <select 
                      id='forceAllUnks'
                      value={this.state.forceAllUnks}
                      onChange={(e) => this.handleInputChange('forceAllUnks', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceBrowserAsr'>
                      Force Browser ASR
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Override the Browser ASR license check for this session. When true, bypasses the district license requirement so on-device ASR can be tested without a licensed district. Leave unset to use the real license value.</span>
                      </span>
                    </label>
                    <select
                      id='forceBrowserAsr'
                      value={this.state.forceBrowserAsr}
                      onChange={(e) => this.handleInputChange('forceBrowserAsr', e.target.value)}
                    >
                      <option value=''>-- Use real license --</option>
                      <option value='true'>true</option>
                      <option value='false'>false</option>
                    </select>
                  </div>


                  <div className='debug-menu-field'>
                    <label htmlFor='forceTapToRead'>
                      Force Tap-to-Read Mode
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force tap-to-read mode on for this session, regardless of feature flag/experiment assignment.</span>
                      </span>
                    </label>
                    <select
                      id='forceTapToRead'
                      value={this.state.forceTapToRead}
                      onChange={(e) => this.handleInputChange('forceTapToRead', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceCursorFree'>
                      Force Cursor-Free Mode
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Force cursor-free (&quot;cursor off&quot;) tutor mode on or off for this session, bypassing the district threshold / WCPM / onboarding gating. Leave unset to use the real eligibility logic. Does not override early-reader or tap-to-read.</span>
                      </span>
                    </label>
                    <select
                      id='forceCursorFree'
                      value={this.state.forceCursorFree}
                      onChange={(e) => this.handleInputChange('forceCursorFree', e.target.value)}
                    >
                      <option value=''>-- Use real behavior --</option>
                      <option value='true'>true</option>
                      <option value='false'>false</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceBadSignalCursor'>
                      Force Bad-Signal Cursor Branch
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Override the district entitlement for the bad-signal cursor branch (TT-991), which changes the cursor after an activity with bad audio or bad network. true turns the branch on for this session, false turns it off even on a district that has it. Leave unset to use the real entitlement.</span>
                      </span>
                    </label>
                    <select
                      id='forceBadSignalCursor'
                      value={this.state.forceBadSignalCursor}
                      onChange={(e) => this.handleInputChange('forceBadSignalCursor', e.target.value)}
                    >
                      <option value=''>-- Use real entitlement --</option>
                      <option value='true'>true</option>
                      <option value='false'>false</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceBadSignalLogOnly'>
                      Bad-Signal Branch: Log Only
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Override TT-991&apos;s dark-launch build constant (BAD_SIGNAL_LOG_ONLY). true runs the whole branch and logs the decision to cursor_bad_signal_decision without changing the cursor. false only APPLIES the decision when the branch is also opened by a license or by Bad-Signal Cursor = true; on a build with no district license set, false alone turns the branch off entirely (no classification, no log) -- pair it with Bad-Signal Cursor = true to actually apply. Leave unset to use the build&apos;s value.</span>
                      </span>
                    </label>
                    <select
                      id='forceBadSignalLogOnly'
                      value={this.state.forceBadSignalLogOnly}
                      onChange={(e) => this.handleInputChange('forceBadSignalLogOnly', e.target.value)}
                    >
                      <option value=''>-- Use real setting --</option>
                      <option value='true'>true</option>
                      <option value='false'>false</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceLocalAsrWarmup'>
                      Bypass ASR Warmup Gate
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>When on, the warmup still runs and logs telemetry but a &quot;slow&quot; verdict will not route phrases to the server. Use to test local ASR on borderline devices.</span>
                      </span>
                    </label>
                    <select
                      id='forceLocalAsrWarmup'
                      value={this.state.forceLocalAsrWarmup}
                      onChange={(e) => this.handleInputChange('forceLocalAsrWarmup', e.target.value)}
                    >
                      <option value='false'>false</option>
                      <option value='true'>true</option>
                    </select>
                  </div>

                  <div className='debug-menu-field'>
                    <label htmlFor='forceBrand'>
                      Force Brand
                      <span className='debug-menu-tooltip'>
                        <FaInfoCircle />
                        <span className='debug-menu-tooltiptext'>Override the session branding. Useful for testing brand-specific features like TTS engine selection.</span>
                      </span>
                    </label>
                    <select 
                      id='forceBrand'
                      value={this.state.forceBrand}
                      onChange={(e) => this.handleInputChange('forceBrand', e.target.value)}
                    >
                      <option value=''>-- Use Default --</option>
                      <option value='amira'>Amira</option>
                      <option value='hmh'>HMH</option>
                      <option value='nwea-map-tutor'>NWEA Map Tutor</option>
                      <option value='eps'>EPS</option>
                      <option value='eps-standalone-reading-assistant'>EPS Standalone</option>
                      <option value='highlights'>Highlights</option>
                    </select>
                  </div>
                </div>
                <button
                  className='debug-menu-save-button'
                  onClick={this.handleSave}
                >
                  Save and Reload
                </button>
              </div>

              <div className='debug-menu-export-section'>
                <h3 className='debug-menu-section-title'>Local Development</h3>
                <p className='debug-menu-export-info'>
                  When enabled, activity launches on this browser are redirected to
                  http://localhost:8080 with the full login URL (see README &quot;Testing
                  Locally with Dev Environment&quot;). Takes effect immediately — not part
                  of Save and Reload. If localhost isn&apos;t running, launches will fail
                  until you turn this off.
                </p>
                <label className='debug-menu-localhost-toggle'>
                  <input
                    type='checkbox'
                    checked={this.state.redirectToLocalhost}
                    onChange={this.handleRedirectToLocalhostChange}
                  />
                  {' '}Redirect launches to localhost:8080
                </label>
                <label className='debug-menu-localhost-toggle'>
                  <input
                    type='checkbox'
                    checked={this.state.showLocalIndicator}
                    onChange={this.handleShowLocalIndicatorChange}
                  />
                  {' '}Show &quot;LOCAL&quot; bubble when running on localhost
                </label>
              </div>

              {(FEATURE_FLAGS.ENABLE_LOCAL_ASR || this.state.forceBrowserAsr === 'true') && (
              <div className='debug-menu-export-section'>
                <h3 className='debug-menu-section-title'>ASR Runtime Diagnostics</h3>
                <div className='debug-menu-session-info'>
                  <div className='debug-menu-info-row'>
                    <label>Cross-Origin Isolated (COOP/COEP)</label>
                    <span className='debug-menu-info-value'>{this.state.asrRuntimeCaps.crossOriginIsolated ? 'Yes' : 'No'}</span>
                  </div>
                  <div className='debug-menu-info-row'>
                    <label>SharedArrayBuffer</label>
                    <span className='debug-menu-info-value'>{this.state.asrRuntimeCaps.sharedArrayBuffer ? 'Available' : 'Unavailable'}</span>
                  </div>
                  <div className='debug-menu-info-row'>
                    <label>WASM Threading</label>
                    <span className={`debug-menu-info-value${!(this.state.asrRuntimeCaps.crossOriginIsolated && this.state.asrRuntimeCaps.sharedArrayBuffer) ? ' debug-menu-info-warning' : ''}`}>
                      {this.state.asrRuntimeCaps.crossOriginIsolated && this.state.asrRuntimeCaps.sharedArrayBuffer ? 'Multi-threaded' : 'Single-threaded (3-4x slower)'}
                    </span>
                  </div>
                  <div className='debug-menu-info-row'>
                    <label>WASM SIMD</label>
                    <span className='debug-menu-info-value'>{this.state.asrRuntimeCaps.simd ? 'Supported' : 'Not supported'}</span>
                  </div>
                  <div className='debug-menu-info-row'>
                    <label>WebGPU</label>
                    <span className='debug-menu-info-value'>{this.state.asrRuntimeCaps.webGpu ? 'Available' : 'Not available'}</span>
                  </div>
                  <div className='debug-menu-info-row'>
                    <label>Hardware Concurrency</label>
                    <span className='debug-menu-info-value'>{this.state.asrRuntimeCaps.hardwareConcurrency} cores</span>
                  </div>
                </div>
              </div>
              )}

              {(FEATURE_FLAGS.ENABLE_LOCAL_ASR || this.state.forceBrowserAsr === 'true') && (
              <div className='debug-menu-export-section'>
                <h3 className='debug-menu-section-title'>Browser ASR Decode Mode</h3>
                <p className='debug-menu-export-info'>
                  Streaming decode (each stride heard once) is the default. Enable window mode to restore the legacy rolling-window + consensus path. Takes effect after Save and Reload.
                </p>
                <label className='debug-menu-localhost-toggle'>
                  <input
                    type='checkbox'
                    checked={this.state.asrWindowMode}
                    onChange={(e) => this.setState({ asrWindowMode: e.target.checked })}
                  />
                  {' '}Use legacy rolling-window decode (window mode)
                </label>
              </div>
              )}

              {(FEATURE_FLAGS.ENABLE_LOCAL_ASR || this.state.forceBrowserAsr === 'true') && (
              <div className='debug-menu-export-section'>
                <h3 className='debug-menu-section-title'>Browser ASR Consensus Tuning</h3>
                <p className='debug-menu-export-info'>
                  Adjust streaming consensus thresholds (window mode only). Changes take effect after Save and Reload (applied to next phrase).
                </p>
                <div className='debug-menu-form'>
                  {Object.keys(CONSENSUS_DEFAULTS).map(key => (
                    <div className='debug-menu-field' key={key}>
                      <label htmlFor={`consensus-${key}`}>
                        {key}
                        {this.state.consensusValues[key] !== CONSENSUS_DEFAULTS[key] && (
                          <span className='debug-menu-info-warning'> (modified)</span>
                        )}
                      </label>
                      <input
                        id={`consensus-${key}`}
                        type='number'
                        step='0.05'
                        value={this.state.consensusValues[key]}
                        onChange={(e) => this.handleConsensusChange(key, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <button
                  className='debug-menu-save-button'
                  onClick={() => this.handleConsensusReset()}
                  style={{ marginTop: '8px', background: '#666' }}
                >
                  Reset to Defaults
                </button>
              </div>
              )}
              
              <div className='debug-menu-export-section'>
                <h3 className='debug-menu-export-title'>Local ASR assets</h3>
                <p className='debug-menu-export-info'>
                  Clear cached model/runtime files. Reload the page manually to re-test the loading progress bar.
                </p>
                <button
                  className={`debug-menu-clear-asr-button ${this.state.isClearingAsrCache ? 'clearing' : ''}`}
                  onClick={this.handleClearAsrAssetCache}
                  disabled={this.state.isClearingAsrCache}
                >
                  {this.state.isClearingAsrCache ? (
                    <>
                      <span className='debug-menu-spinner'></span>
                      Clearing...
                    </>
                  ) : (
                    'Clear ASR asset cache'
                  )}
                </button>
              </div>

            </div>
          </div>
        )}
      </>
    );
  }
}

/**
 * Maps Redux state to component props
 * Retrieves the current story ID from the readerManager Redux slice
 * Tries currentStoryId first, then falls back to currentStory.storyId
 */
const mapStateToProps = (state) => {
  const contextStates = state.readerManager?.contextStates;
  const currentStoryId = contextStates?.currentStoryId || 
                         contextStates?.currentStory?.storyId || 
                         null;
  return { currentStoryId };
};

export default connect(mapStateToProps)(DebugMenu);

